import { css } from "@emotion/react";
import { InfoOutlined } from "@mui/icons-material";
import { Theme, useTheme } from "@mui/material";
import Image from "next/image";
import Link from "next/link";

import CategoryPill from "~/components/common/category/CategoryPill";
import RaidProgressBar from "~/components/common/raid/raid-progress-bar";
import { Container1200pxWidth } from "~/components/common/sized-containers";
import { BANNER_IMAGE_SIZE } from "~/components/raid/lib";
import { TreasuryRaidLink } from "~/extensions/hojokin";
import { engFont } from "~/lib/client/fonts";
import { useRaidDetailContext } from "~/pages/raid/[id]";

const PcRaidDetail = () => {
  const { raid, generatedAt } = useRaidDetailContext();
  const theme = useTheme();
  const achievedRate = Math.floor(
    (raid.category.accumulatedPoint / raid.achievementPoint) * 100
  );
  return (
    <main>
      <div css={raidMainVisualStyle}>
        <Container1200pxWidth>
          <Image
            src={raid.bannerImageUrl}
            alt={raid.title}
            width={BANNER_IMAGE_SIZE.width}
            height={BANNER_IMAGE_SIZE.height}
            sizes="100vw"
            style={{
              width: "1200px",
              height: "auto",
            }}
          />
          <h1 css={raidMainInfoStyle} className={engFont.className}>
            <div css={raidStagePointStyle}>
              <div css={stageStyle}>{raid.stage.ordinal()} Stage</div>
              <div css={raidPointStyle}>
                <div css={goalPointStyle}>GOAL</div>
                <div css={raidAchievementPointStyle}>
                  {raid.achievementPoint.toLocaleString()}
                </div>
              </div>
            </div>
            <p css={raidTitleStyle}>{raid.title}</p>
          </h1>
          <RaidProgressBar
            achievedRate={achievedRate}
            point={raid.category.accumulatedPoint.toLocaleString()}
          />
        </Container1200pxWidth>
      </div>
      <Container1200pxWidth>
        <div css={faveContentContainer}>
          <p>Fave</p>
          <Link href={`/category/${raid.category.id}`}>
            <CategoryPill name={raid.category.name} />
          </Link>
        </div>
        <article css={raidDescriptionStyle(theme)}>
          <div dangerouslySetInnerHTML={{ __html: raid.description }} />
        </article>
        <p css={supplementaryInfoStyle(theme)}>
          <InfoOutlined />
          このページは1時間毎に更新されます。（最終更新: {generatedAt}）
        </p>
        <Link
          href={`/category/${raid.category.id}`}
          css={leadToCategoryPageStyle}
        >
          <span css={categoryNameStyle}>{raid.category.name}</span>
          のFave ページへ
        </Link>
        <p css={archivedRaidStyle} className={engFont.className}>
          Archived Projects
        </p>
        {raid.category.raids
          .filter((r) => r.id != raid.id)
          .sort((a, b) => b.stage - a.stage)
          .map((archivedRaid) => (
            <Link
              href={`/raid/${archivedRaid.id}`}
              key={archivedRaid.id}
              css={achievedRaidLinkStyle}
            >
              <Image
                src={archivedRaid.bannerImageUrl}
                alt={archivedRaid.title}
                width={BANNER_IMAGE_SIZE.width / 4}
                height={BANNER_IMAGE_SIZE.height / 4}
              />

              <div
                css={css`
                  margin-left: 3rem;
                `}
              >
                <div css={archivedRaidStageStyle} className={engFont.className}>
                  {archivedRaid.stage.ordinal()} Stage
                </div>
                <h2 css={archivedRaidTitleStyle}>{archivedRaid.title}</h2>
              </div>
            </Link>
          ))}
        <TreasuryRaidLink />
      </Container1200pxWidth>
    </main>
  );
};

const raidMainVisualStyle = css`
  width: 100%;
  background: #f6f6f6;
  padding-bottom: 2rem;
`;

const raidMainInfoStyle = css`
  padding: 2rem;
`;

const stageStyle = css`
  font-weight: 700;
  font-size: 40px;
`;

const raidStagePointStyle = css`
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: space-between;
`;

const raidPointStyle = css`
  display: flex;
  flex-flow: column;
  align-items: center;
`;

const goalPointStyle = css`
  font-size: 12px;
  font-weight: 400;
`;

const raidAchievementPointStyle = css`
  font-size: 18px;
  font-weight: 700;
`;

const raidTitleStyle = css`
  font-size: 30px;
  font-weight: 700;
`;

const faveContentContainer = css`
  display: flex;
  flex-flow: row;
  gap: 2rem;
  align-items: center;
`;

const raidDescriptionStyle = (theme: Theme) => css`
  padding: 1rem;
  color: ${theme.palette.text.primary};
  white-space: pre-wrap;
  word-break: break-word;

  h2 {
    margin: 0;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
  }

  a {
    color: ${theme.palette.primary.main};
    text-decoration: underline;
  }
`;

const supplementaryInfoStyle = (theme: Theme) => css`
  display: flex;
  flex-flow: row;
  justify-content: flex-end;
  text-align: center;
  align-items: center;
  color: ${theme.palette.text.secondary};
  font-size: 0.8rem;
  gap: 0.25rem;
  padding-right: 0.5rem;
`;

const leadToCategoryPageStyle = css`
  margin-top: 4rem;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const categoryNameStyle = css`
  font-size: 20px;
  font-weight: 600;
  text-decoration: underline;
`;

const achievedRaidLinkStyle = css`
  display: flex;
  flex-flow: row;
  align-items: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-top: 1px solid #eee;
`;

const archivedRaidStyle = css`
  font-size: 14px;
  font-weight: 700;
`;

const archivedRaidStageStyle = css`
  font-size: 12px;
  font-weight: 700;
`;

const archivedRaidTitleStyle = css`
  font-size: 20px;
  font-weight: 700;
`;

export default PcRaidDetail;
